// The fisherman rig, spawn placement, orbit camera, and on-foot movement.
// Extracted from legacy/tsuri.html — logic and constants are unchanged.
//
// Boat-rowing movement is NOT handled here: in the legacy game, moving
// while aboard the boat drives the boat instead of the on-foot walk cycle.
// That branch belongs with the boat (building.ts, step 8) and gets wired
// up in main.ts (step 10) once it exists.

import * as THREE from 'three';
import { SEA_LEVEL, PEAK_LEVEL, PROP_SCALE, hexToWorld, axialDistance, terrainHeightAt } from '@world/terrain';
import { toonMat, addOutlines } from '@world/toon';
import { fishGeo, fishMatFor } from '@entities/fish';
import { AVATARS, type AvatarVisual } from '@entities/avatars';

const IS_TOUCH = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;

export const player = { x: 0, z: 0, heading: 0, walking: false, walkPhase: 0 };

function angleDiff(a: number, b: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}

// ================= The fisherman rig =================
// Old-school Japanese fisherman: indigo kimono, straw kasa hat, white
// beard, bamboo rod. All simple flat-shaded primitives (7 meshes).
export function buildFisherman(visual: AvatarVisual = AVATARS[0].visual) {
  const fisher = new THREE.Group();
  const kimonoMat = toonMat(visual.kimonoColor);
  const sashMat = toonMat(visual.sashColor);
  const skinMat = toonMat(0xd3a880);
  const beardMat = toonMat(visual.beardColor);
  const strawMat = toonMat(visual.hatColor);
  const bambooMat = toonMat(0xb8a877);

  // Kimono robe (tapered)
  const robe = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.24, 0.52, 7), kimonoMat);
  robe.position.y = 0.26;
  fisher.add(robe);
  // Obi sash
  const sash = new THREE.Mesh(new THREE.CylinderGeometry(0.155, 0.165, 0.06, 7), sashMat);
  sash.position.y = 0.36;
  fisher.add(sash);
  // Head
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.105, 7, 5), skinMat);
  head.position.y = 0.62;
  fisher.add(head);
  // Beard (small squashed blob on the front of the face; front = +X) —
  // the "older" avatars' marker; hidden entirely for clean-shaven ones.
  const beard = new THREE.Mesh(new THREE.IcosahedronGeometry(0.055, 0), beardMat);
  beard.scale.set(1, 1.5 * visual.beardScale, 1);
  beard.position.set(0.08, 0.56, 0);
  beard.visible = visual.beardScale > 0;
  fisher.add(beard);
  // Sugegasa: shallow woven cone with a bound apex, a rim band at the brim
  // edge and a chin cord. The trim parts hang off the cone rather than
  // sitting in their own Group so `hat` stays a Mesh — setLocalAvatarVisual
  // recolours it via hat.material.
  const hat = new THREE.Mesh(new THREE.ConeGeometry(0.26, 0.14, 12), strawMat);
  hat.position.y = 0.73;
  hat.rotation.x = 0.06;
  const hatRim = new THREE.Mesh(new THREE.TorusGeometry(0.252, 0.015, 4, 12), strawMat);
  hatRim.rotation.x = Math.PI / 2;
  hatRim.position.y = -0.065;
  hat.add(hatRim);
  const hatKnob = new THREE.Mesh(new THREE.SphereGeometry(0.023, 6, 4), strawMat);
  hatKnob.position.y = 0.075;
  hat.add(hatKnob);
  const cordMat = toonMat(0x4a4038);
  ([-1, 1] as const).forEach((side) => {
    const cord = new THREE.Mesh(new THREE.CylinderGeometry(0.005, 0.005, 0.15, 4), cordMat);
    cord.position.set(0.015, -0.135, side * 0.08);
    cord.rotation.x = side * -0.3;
    hat.add(cord);
  });
  fisher.add(hat);
  // Woven basket on his back, showing the fish he's carrying
  const basketMat = toonMat(0xa98d5f);
  const basket = new THREE.Mesh(new THREE.CylinderGeometry(0.085, 0.062, 0.17, 6), basketMat);
  basket.position.set(-0.15, 0.42, 0);
  basket.rotation.z = 0.18;
  fisher.add(basket);
  const basketSlots: THREE.Mesh[] = [];
  for (let i = 0; i < 5; i++) {
    const f = new THREE.Mesh(fishGeo, fishMatFor(''));
    f.scale.setScalar(0.09);
    const a = i * 1.25;
    f.position.set(-0.15 + Math.cos(a) * 0.035, 0.51 + (i % 2) * 0.015, Math.sin(a) * 0.045);
    f.rotation.set(0.9, a, 0);
    f.visible = false;
    basketSlots.push(f);
    fisher.add(f);
  }
  // Bamboo fishing rod held in the right hand, angled forward-up
  const rodMesh = new THREE.Mesh(new THREE.CylinderGeometry(0.008, 0.016, 1.15, 5), bambooMat);
  rodMesh.position.set(0.16, 0.42, 0.13);
  rodMesh.rotation.z = -0.85; // tilt toward +X (forward)
  fisher.add(rodMesh);
  // Invisible marker at the rod tip for attaching the line
  const rodTipMarker = new THREE.Object3D();
  rodTipMarker.position.y = 0.575;
  rodMesh.add(rodTipMarker);
  // Hand axe in the left hand — hidden until he chops
  const ironMat = toonMat(0x99a0a4);
  const axeGroup = new THREE.Group();
  const axeHandle = new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.018, 0.38, 5), bambooMat);
  axeHandle.position.y = 0.19;
  axeGroup.add(axeHandle);
  const axeHead = new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.07, 0.028), ironMat);
  axeHead.position.set(0.055, 0.35, 0);
  axeGroup.add(axeHead);
  const axeEdge = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.09, 0.02), ironMat);
  axeEdge.position.set(0.125, 0.35, 0);
  axeGroup.add(axeEdge);
  axeGroup.position.set(0.15, 0.36, 0.13);
  axeGroup.visible = false;
  fisher.add(axeGroup);

  // Kizuchi: the big wooden mallet he drives pegs with when repairing.
  // Same hand, same swing as the axe — only the head differs.
  const malletGroup = new THREE.Group();
  const malletHandle = new THREE.Mesh(new THREE.CylinderGeometry(0.016, 0.02, 0.4, 5), bambooMat);
  malletHandle.position.y = 0.2;
  malletGroup.add(malletHandle);
  const malletHead = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.055, 0.17, 8), toonMat(0x8a6b45));
  malletHead.rotation.z = Math.PI / 2;
  malletHead.position.set(0.02, 0.38, 0);
  malletGroup.add(malletHead);
  malletGroup.position.set(0.15, 0.36, 0.13);
  malletGroup.visible = false;
  fisher.add(malletGroup);

  fisher.scale.set(
    visual.buildScale * PROP_SCALE,
    visual.heightScale * PROP_SCALE,
    visual.buildScale * PROP_SCALE,
  );
  addOutlines(fisher);

  return { fisher, rodTipMarker, axeGroup, malletGroup, rodMesh, basketSlots, robe, sash, head, beard, hat, basket };
}

const localRig = buildFisherman();
export const { fisher, rodTipMarker, axeGroup, malletGroup, rodMesh, basketSlots } = localRig;

// Restyles the local player's rig in place (materials + scale) — called
// once when a game actually starts, not on every avatar-row click.
// Remote players don't need this: remotePlayers.ts builds each one fresh
// with buildFisherman(visual) directly.
export function setLocalAvatarVisual(visual: AvatarVisual): void {
  (localRig.robe.material as THREE.MeshToonMaterial).color.set(visual.kimonoColor);
  (localRig.sash.material as THREE.MeshToonMaterial).color.set(visual.sashColor);
  (localRig.hat.material as THREE.MeshToonMaterial).color.set(visual.hatColor);
  (localRig.beard.material as THREE.MeshToonMaterial).color.set(visual.beardColor);
  localRig.beard.scale.set(1, 1.5 * visual.beardScale, 1);
  localRig.beard.visible = visual.beardScale > 0;
  // Must include PROP_SCALE: this runs when a game starts and would
  // otherwise overwrite the scale buildFisherman set, putting the fisherman
  // back to full size in-world.
  fisher.scale.set(
    visual.buildScale * PROP_SCALE,
    visual.heightScale * PROP_SCALE,
    visual.buildScale * PROP_SCALE,
  );
}

// A bare dry-land hex isn't enough — some seeds' nearest-to-origin land is
// a sliver of shore with no wood anywhere nearby (a real case: Heron Lake,
// seed 2136, spawns in the middle of a huge lake with zero tree-eligible
// hexes within 20+ rings, making it impossible to ever build a jetty).
// Require a modest amount of tree-eligible land (matches chunks.ts's
// SEA_LEVEL+2 threshold for tree spawning) within an easy walk.
const MIN_NEARBY_TREE_HEXES = 5;
const TREE_CHECK_RADIUS = 10;

function hasNearbyTrees(q: number, r: number): boolean {
  let count = 0;
  for (let dq = -TREE_CHECK_RADIUS; dq <= TREE_CHECK_RADIUS; dq++) {
    for (let dr = -TREE_CHECK_RADIUS; dr <= TREE_CHECK_RADIUS; dr++) {
      if (axialDistance(0, 0, dq, dr) > TREE_CHECK_RADIUS) continue;
      const h = terrainHeightAt(q + dq, r + dr);
      if (h >= SEA_LEVEL + 2 && h < PEAK_LEVEL) {
        count++;
        if (count >= MIN_NEARBY_TREE_HEXES) return true;
      }
    }
  }
  return false;
}

// Spawn on the nearest dry-land hex to the origin that also has enough
// nearby land to actually bootstrap the game (runs once the seed is set).
export function findSpawnPoint(): void {
  for (let radius = 0; radius < 40; radius++) {
    for (let dq = -radius; dq <= radius; dq++) {
      const rMin = Math.max(-radius, -dq - radius), rMax = Math.min(radius, -dq + radius);
      for (let dr = rMin; dr <= rMax; dr++) {
        if (axialDistance(0, 0, dq, dr) !== radius) continue;
        const h = terrainHeightAt(dq, dr);
        if (h >= SEA_LEVEL + 1 && h < PEAK_LEVEL && hasNearbyTrees(dq, dr)) {
          const pos = hexToWorld(dq, dr);
          player.x = pos.x; player.z = pos.z;
          return;
        }
      }
    }
  }
}

// ================= Tool swing animation =================
// One swing drives both tools — the axe for chopping, the mallet for
// repairs — since the motion is the same overhead arc either way.
export const CHOP_ANIM_LEN = 0.5;
let chopTime = 0;
let swingTool: 'axe' | 'mallet' = 'axe';

export function startChopSwing(): void {
  chopTime = CHOP_ANIM_LEN;
  swingTool = 'axe';
}

export function startRepairSwing(): void {
  chopTime = CHOP_ANIM_LEN;
  swingTool = 'mallet';
}

export function updateChop(dt: number): void {
  if (chopTime <= 0) {
    axeGroup.visible = false;
    malletGroup.visible = false;
    rodMesh.visible = true;
    return;
  }
  chopTime -= dt;
  const swinging = chopTime > 0;
  const tool = swingTool === 'axe' ? axeGroup : malletGroup;
  axeGroup.visible = swinging && swingTool === 'axe';
  malletGroup.visible = swinging && swingTool === 'mallet';
  rodMesh.visible = !swinging; // he puts the rod down to work
  const t = 1 - Math.max(0, chopTime) / CHOP_ANIM_LEN; // 0 → 1 over the swing
  // Wind up over the shoulder, then swing down at the work
  const swing = t < 0.35 ? (t / 0.35) * 0.9 : 0.9 - ((t - 0.35) / 0.65) * 2.1;
  tool.rotation.z = swing;
}

// ================= Camera controls (mouse + touch via Pointer Events) =================
export const mouseNDC = new THREE.Vector2(0, 0);

// Camera distances track PROP_SCALE so the fisherman keeps the same
// on-screen size he had before the props were scaled down. The world is
// unchanged in world units, so pulling in three times closer means a hex
// now fills three times as much screen — which is the intent: a closer,
// tile-sized view rather than a wide one.
const CAM_DIST_MIN = 3 * PROP_SCALE;
const CAM_DIST_MAX = 11 * PROP_SCALE;
const CAM_TARGET_Y = 0.55 * PROP_SCALE; // roughly the fisherman's chest
let camYaw = 0.6, camPitch = 0.55, camDist = 5.5 * PROP_SCALE;

export function getCamYaw(): number {
  return camYaw;
}

export const camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.1, 1000);

export function updateCamera(): void {
  const cp = Math.cos(camPitch), sp = Math.sin(camPitch);
  const target = new THREE.Vector3(player.x, fisher.position.y + CAM_TARGET_Y, player.z);
  camera.position.set(
    target.x + Math.sin(camYaw) * cp * camDist,
    target.y + sp * camDist,
    target.z + Math.cos(camYaw) * cp * camDist
  );
  camera.lookAt(target);
}

export interface CameraControlHooks {
  isStarted: () => boolean;
  onTap: () => void;
}

export function initCameraControls(canvas: HTMLCanvasElement, hooks: CameraControlHooks): void {
  const pointers = new Map<number, { x: number; y: number }>();
  let lookPointerId: number | null = null;
  let dragDistance = 0;
  let pinchStartDist = 0, pinchStartCam = camDist;

  function setNDCFromEvent(e: PointerEvent): void {
    const rect = canvas.getBoundingClientRect();
    mouseNDC.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    mouseNDC.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
  }

  canvas.addEventListener('pointerdown', (e) => {
    if (!hooks.isStarted()) return;
    canvas.setPointerCapture(e.pointerId);
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 1) {
      lookPointerId = e.pointerId;
      dragDistance = 0;
      canvas.classList.add('dragging');
    } else if (pointers.size === 2) {
      // Second finger down: switch from look to pinch-zoom
      const pts = Array.from(pointers.values());
      pinchStartDist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      pinchStartCam = camDist;
      lookPointerId = null;
    }
  });

  canvas.addEventListener('pointermove', (e) => {
    setNDCFromEvent(e); // keeps aiming (jetty ghost, raycasts) current on hover and touch alike
    if (!hooks.isStarted()) return;
    const p = pointers.get(e.pointerId);
    if (!p) return;
    const dx = e.clientX - p.x, dy = e.clientY - p.y;
    p.x = e.clientX; p.y = e.clientY;
    if (pointers.size === 2) {
      const pts = Array.from(pointers.values());
      const d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      if (pinchStartDist > 0 && d > 0) {
        camDist = Math.max(CAM_DIST_MIN, Math.min(CAM_DIST_MAX, pinchStartCam * (pinchStartDist / d)));
      }
      return;
    }
    if (e.pointerId === lookPointerId) {
      camYaw -= dx * 0.0032;
      camPitch += dy * 0.003;
      camPitch = Math.max(0.12, Math.min(1.35, camPitch));
      dragDistance += Math.abs(dx) + Math.abs(dy);
    }
  });

  function releasePointer(e: PointerEvent): void {
    pointers.delete(e.pointerId);
    if (e.pointerId === lookPointerId) {
      lookPointerId = null;
      canvas.classList.remove('dragging');
      if (hooks.isStarted() && dragDistance < 8) {
        setNDCFromEvent(e); // tap position = interaction target
        hooks.onTap();
      }
    }
    if (pointers.size < 2) pinchStartDist = 0;
  }
  canvas.addEventListener('pointerup', releasePointer);
  canvas.addEventListener('pointercancel', releasePointer);

  canvas.addEventListener('wheel', (e) => {
    if (!hooks.isStarted()) return;
    // Wheel sensitivity scales too, or one notch would cross the whole
    // (now three times narrower) zoom range.
    camDist = Math.max(CAM_DIST_MIN, Math.min(CAM_DIST_MAX, camDist + e.deltaY * 0.004 * PROP_SCALE));
  }, { passive: true });
  canvas.addEventListener('contextmenu', (e) => e.preventDefault());
}

// ================= Movement input: keyboard + virtual joystick =================
const keys: Record<string, boolean> = {};

export function initKeyboardControls(): void {
  document.addEventListener('keydown', (e) => { keys[e.code] = true; });
  document.addEventListener('keyup', (e) => { keys[e.code] = false; });
}

const joy = { x: 0, y: 0 };

// Virtual joystick (shown on touch devices once the game starts)
export function initJoystickControls(joystickEl: HTMLElement, knobEl: HTMLElement): void {
  if (!IS_TOUCH) return;
  const R = 42;
  let jid: number | null = null;
  function setKnob(dx: number, dy: number): void {
    knobEl.style.transform = 'translate(calc(-50% + ' + dx + 'px), calc(-50% + ' + dy + 'px))';
  }
  function track(e: PointerEvent): void {
    const rect = joystickEl.getBoundingClientRect();
    let dx = e.clientX - (rect.left + rect.width / 2);
    let dy = e.clientY - (rect.top + rect.height / 2);
    const d = Math.hypot(dx, dy);
    if (d > R) { dx *= R / d; dy *= R / d; }
    setKnob(dx, dy);
    joy.x = dx / R;
    joy.y = dy / R;
  }
  joystickEl.addEventListener('pointerdown', (e) => {
    jid = e.pointerId;
    joystickEl.setPointerCapture(e.pointerId);
    track(e);
  });
  joystickEl.addEventListener('pointermove', (e) => { if (e.pointerId === jid) track(e); });
  function end(e: PointerEvent): void {
    if (e.pointerId !== jid) return;
    jid = null;
    joy.x = 0; joy.y = 0;
    setKnob(0, 0);
  }
  joystickEl.addEventListener('pointerup', end);
  joystickEl.addEventListener('pointercancel', end);
}

function readMovementAxes(): { ix: number; iz: number } {
  let ix = 0, iz = 0;
  if (keys['KeyW']) iz += 1;
  if (keys['KeyS']) iz -= 1;
  if (keys['KeyD']) ix += 1;
  if (keys['KeyA']) ix -= 1;
  ix += joy.x;
  iz -= joy.y; // joystick up = forward
  return { ix, iz };
}

export const WALK_SPEED = 2.6;

// Crossing from dry land into the water takes a moment of holding the
// direction — the step into a water-only tile ramps up from a cautious
// creep to full pace over this many seconds, rather than being flatly
// blocked then suddenly released, so it reads as wading in tentatively
// (still feels seamless) instead of hitting a wall. Already being in the
// water needs no ramp: once you're in, you move around at full pace.
const WADE_ENTRY_HOLD = 0.35;
let wadeHoldTimer = 0;

export interface MoveVector {
  dx: number;
  dz: number;
  len: number;
  moving: boolean;
}

// Camera-relative movement vector from keyboard/joystick input. Exported
// so main.ts's boat-rowing branch (not player movement, so it's not in
// this file — see the module header) can drive the boat with the same
// input reading updatePlayerOnFoot uses for walking.
export function computeMoveVector(sleeping: boolean): MoveVector {
  const fwd = { x: -Math.sin(camYaw), z: -Math.cos(camYaw) };
  const rgt = { x: Math.cos(camYaw), z: -Math.sin(camYaw) };
  const { ix, iz } = readMovementAxes();
  const dx = fwd.x * iz + rgt.x * ix;
  const dz = fwd.z * iz + rgt.z * ix;
  const len = Math.hypot(dx, dz);
  const moving = len > 0.05 && !sleeping;
  return { dx, dz, len, moving };
}

export interface OnFootMoveOptions {
  canStand: (x: number, z: number) => boolean; // dry land / jetty deck only
  canWade: (fromX: number, fromZ: number, toX: number, toZ: number) => boolean; // + shallowest water
  standY: (x: number, z: number) => number;
  speedMult: number; // restSpeedMult(), from the rest/sleep system
  sleeping: boolean; // sleepAnim > 0, from the rest/sleep system
  castActive: boolean; // cast.active, from the fishing system
}

export interface OnFootMoveResult {
  interruptedCast: boolean;
}

// Worn/held local transforms for the hat, basket, and rod — captured once
// so the rough-sleep pose below has something to lerp back to on waking.
const HAT_WORN_POS = new THREE.Vector3(0, 0.73, 0);
const HAT_WORN_ROT_X = 0.06;
const HAT_ASIDE_POS = new THREE.Vector3(0.18, 0.06, 0.16); // ground level, beside him
const BASKET_WORN_POS = new THREE.Vector3(-0.15, 0.42, 0);
const BASKET_ASIDE_POS = new THREE.Vector3(-0.22, 0.4, 0.22);
const ROD_WORN_POS = new THREE.Vector3(0.16, 0.42, 0.13);
const ROD_WORN_ROT_Z = -0.85;
const ROD_ASIDE_POS = new THREE.Vector3(0.1, 0.27, 0.52);
const ROD_ASIDE_ROT_Z = -1.55; // near-horizontal, laid on the ground

// Lie down while sleeping, rise smoothly after. Applies regardless of
// on-foot vs. boat-rowing movement (unlike the rest of on-foot movement
// below), so the caller (main.ts) runs this once per frame before
// branching on inBoat — otherwise sleeping rough in the boat never lies
// the fisherman down, since updateBoatRowing never touches this axis.
//
// `rough` (sleepQuality === 'rough') additionally lays the hat over his
// face and sets the basket and rod down beside him, instead of the plain
// whole-body tilt used for collapsing/sleeping in a shelter. The hat and
// rod counter-rotate against the body's own forward pitch (fisher.rotation.x)
// so they settle into a roughly fixed "laid down" orientation instead of
// tumbling along with the body as it lies flat.
export function updateSleepPose(dt: number, sleeping: boolean, rough: boolean): void {
  const t = Math.min(1, dt * 6);
  fisher.rotation.x += ((sleeping ? -1.45 : 0) - fisher.rotation.x) * t;

  const aside = sleeping && rough;
  const { hat, basket } = localRig;

  // Cancel the body's own tilt entirely (rather than tipping it further
  // onto its side) so the hat keeps its normal upright, brim-down profile
  // — the look of a hat resting flat on the sand, not knocked over.
  hat.position.lerp(aside ? HAT_ASIDE_POS : HAT_WORN_POS, t);
  const hatTargetRotX = aside ? -fisher.rotation.x + HAT_WORN_ROT_X : HAT_WORN_ROT_X;
  hat.rotation.x += (hatTargetRotX - hat.rotation.x) * t;

  basket.position.lerp(aside ? BASKET_ASIDE_POS : BASKET_WORN_POS, t);
  const basketTargetRotX = aside ? -fisher.rotation.x : 0;
  basket.rotation.x += (basketTargetRotX - basket.rotation.x) * t;

  rodMesh.position.lerp(aside ? ROD_ASIDE_POS : ROD_WORN_POS, t);
  rodMesh.rotation.z += ((aside ? ROD_ASIDE_ROT_Z : ROD_WORN_ROT_Z) - rodMesh.rotation.z) * t;
  const rodTargetRotX = aside ? -fisher.rotation.x : 0;
  rodMesh.rotation.x += (rodTargetRotX - rodMesh.rotation.x) * t;
}

// On-foot movement: land and jetty decks only. See the module header for
// why boat-rowing movement isn't handled here.
export function updatePlayerOnFoot(dt: number, opts: OnFootMoveOptions): OnFootMoveResult {
  let { dx, dz, len, moving } = computeMoveVector(opts.sleeping);

  player.walking = moving;
  let interruptedCast = false;
  if (moving) {
    dx /= len; dz /= len;
    const step = WALK_SPEED * opts.speedMult * dt;
    // Try full move; if blocked by water, try sliding along each axis.
    const tries: Array<[number, number]> = [
      [player.x + dx * step, player.z + dz * step],
      [player.x + dx * step, player.z],
      [player.x, player.z + dz * step],
    ];

    let holdProgress = 1;
    if (opts.canStand(player.x, player.z)) {
      // On dry land/jetty: only ramps down while the primary candidate is
      // specifically a water-only move (i.e. actually trying to wade in).
      const [tx, tz] = tries[0];
      const enteringWater = !opts.canStand(tx, tz) && opts.canWade(player.x, player.z, tx, tz);
      wadeHoldTimer = enteringWater ? wadeHoldTimer + dt : 0;
      holdProgress = Math.min(1, wadeHoldTimer / WADE_ENTRY_HOLD);
    } else {
      wadeHoldTimer = 0; // already wading — no ramp needed to keep moving
    }
    const rampedStep = step * (0.15 + 0.85 * holdProgress);
    const wadeTries: Array<[number, number]> = [
      [player.x + dx * rampedStep, player.z + dz * rampedStep],
      [player.x + dx * rampedStep, player.z],
      [player.x, player.z + dz * rampedStep],
    ];
    for (let i = 0; i < tries.length; i++) {
      const [nx, nz] = tries[i];
      if (opts.canStand(nx, nz)) { player.x = nx; player.z = nz; break; }
      const [wx, wz] = wadeTries[i];
      if (opts.canWade(player.x, player.z, wx, wz)) { player.x = wx; player.z = wz; break; }
    }
    // Face movement direction (unless mid-cast, when he faces the bobber)
    if (!opts.castActive) {
      const targetHeading = Math.atan2(dz, dx);
      player.heading += angleDiff(player.heading, targetHeading) * Math.min(1, dt * 10);
    }
    if (opts.castActive) interruptedCast = true;
    player.walkPhase += dt * 9;
  }
  // Snap to the walking surface (terrain or jetty deck), smoothly
  const targetY = opts.standY(player.x, player.z);
  fisher.position.x = player.x;
  fisher.position.z = player.z;
  fisher.position.y += (targetY - fisher.position.y) * Math.min(1, dt * 12);
  fisher.rotation.y = -player.heading;
  // Little walk bob / idle sway
  if (player.walking) {
    fisher.rotation.z = Math.sin(player.walkPhase) * 0.05;
    fisher.position.y += Math.abs(Math.sin(player.walkPhase)) * 0.02;
  } else {
    fisher.rotation.z = Math.sin(performance.now() * 0.0012) * 0.015;
  }
  return { interruptedCast };
}
